import { computed, Injectable, signal } from '@angular/core';
import { CorpTranslateService } from '@corporate-tailwind/translation';
import { finalize, interval, Observable, switchMap, takeUntil, takeWhile, tap, timer } from 'rxjs';
import { OrderTrackingDto } from '../../model/order-tracking.model';
import { OrderWizardDto } from '../../model/order-wizard.model';
import { OrderWizardApi } from '../_service/order-wizard.api';
import { OrderWizardSelectionState } from './order-wizard.selection.state';
import { SessionUpdateRequest } from '../../../../../shared/session/session-update-request.model';
import { CurrencyPurchaseDto } from '../../../front-pages/common/fp-order-panel/fp-order-purchase/model/currency-purchase.model';
import { CurrencyPurchaseState } from '../../../front-pages/common/fp-order-panel/fp-order-purchase/state/currency-purchase.state';
import { PickupPointDto } from '../../model/pickup-point.model';
import { CustomerDto } from '../../model/customer.model';
import { TranslateService } from '@ngx-translate/core';
import { OrderDto } from '../../model/order.model';
import { Router } from '@angular/router';
import { nullifyIfEmpty } from 'src/app/shared/helper/object-cleaner.helper';
import { CurrencyDirection } from '../../model/order-currency-direction.model';

@Injectable({ providedIn: 'root' })
export class OrderWizardState {
    // 180000
    // emailValidationPoolingInitialTimeout = 180000;// 8000; //3 minutes

    private readonly _orderWizard = signal<OrderWizardDto | null>(null);
    private readonly _loading = signal<boolean>(false);
    private readonly _loadingCurrencyValue = signal<boolean>(false);
    private readonly _loadingCurrencyCreation = signal<boolean>(false);
    private readonly _stripeRedirectUrl = signal<string | null>(null);

    private readonly _loadingEmailValidation = signal<boolean>(false);
    private readonly _emailValidationIsSent = signal<boolean>(false);
    private readonly _emailValidationPoolingCountdown = signal<number>(0); // 3 minutes
    private readonly _emailValidationIsTimeOut = signal<boolean>(false);

    public readonly orderWizard = this._orderWizard.asReadonly();
    public readonly stripeRedirectUrl = this._stripeRedirectUrl.asReadonly();
    public readonly loading = this._loading.asReadonly();
    public readonly loadingCurrencyValue = this._loadingCurrencyValue.asReadonly();
    public readonly loadingCurrencyCreation = this._loadingCurrencyCreation.asReadonly();

    public readonly loadingEmailValidation = this._loadingEmailValidation.asReadonly();
    public readonly emailValidationIsSent = this._emailValidationIsSent.asReadonly();
    public readonly emailValidationPoolingCountdown = this._emailValidationPoolingCountdown.asReadonly();
    public readonly emailValidationIsTimeOut = this._emailValidationIsTimeOut.asReadonly();

    currencyPurchase$ = computed(() => this._currencyPurchaseState.currencyPurchase());

    constructor(
        private _orderWizardApi: OrderWizardApi,
        private _orderWizardSelectionState: OrderWizardSelectionState,
        private _corpTranslateService: CorpTranslateService,
        private _translateService: TranslateService,
        private _currencyPurchaseState: CurrencyPurchaseState,
        private _router: Router,
    ) {
        this._corpTranslateService.attach(this._orderWizard);
    }

    load(orderCode: string) {
        this._loading.set(true);

        this._orderWizardApi
            .load(orderCode)
            .pipe(
                tap(() => this._loading.set(true)),
                finalize(() => this._loading.set(false)),
            )
            .subscribe({
                next: (orderWizard) => {
                    orderWizard = this._corpTranslateService.translate(orderWizard);
                    this._orderWizard.set(orderWizard);

                    //chargement de la selection
                    this._orderWizardSelectionState.load(orderWizard);
                },
                error: (err) => {
                    console.error('Erreur lors du chargement utilisateur', err);
                },
            });
    }

    init() {
        this._loading.set(true);
        this._orderWizard.set(null);
        //recherche du state present dans homePage ->
        let currencyPurchase = this.currencyPurchase$();
        // console.log('mon_orderWizard', currencyPurchase);
        let orderWizardDto: OrderWizardDto | null = null;
        // orderWizardDto.languageCode = this._translateService.getCurrentLang();
        if (currencyPurchase) {
            orderWizardDto = new OrderWizardDto();
            orderWizardDto.languageCode = this._translateService.getCurrentLang();
            orderWizardDto.step01 = new OrderDto();
            orderWizardDto.step01.currencyPurchaseList = [currencyPurchase!];
        }
        // console.log('Init - orderWizardDto ', orderWizardDto);

        this._orderWizardApi
            .init(orderWizardDto)
            .pipe(finalize(() => this._loading.set(false)))
            .subscribe({
                next: (orderWizard) => {
                    //reprise de la langue.
                    if (orderWizard?.languageCode) {
                        this._translateService.use(orderWizard.languageCode);
                    }
                    if (orderWizard?.emailValidationTimeout) {
                        this._emailValidationPoolingCountdown.set(
                            orderWizard?.emailValidationTimeout,
                        );
                    }
                    orderWizard = this._corpTranslateService.translate(orderWizard);
                    this._orderWizard.set(orderWizard);
                    //chargement de la selection
                    this._orderWizardSelectionState.load(orderWizard);
                },
                error: (err) => {
                    console.error('Erreur api', err);
                },
            });
    }

    initiatePayment() {
        this._orderWizardApi
            .initiatePayment()
            .pipe(
                tap(() => this._loading.set(true)),
                finalize(() => this._loading.set(false)),
            )
            .subscribe({
                next: (redirectUrl) => {
                    this._stripeRedirectUrl.set(redirectUrl);
                },
                error: (err) => {
                    console.error('Erreur api', err);
                },
            });
    }

    save(orderWizardDto: OrderWizardDto) {
        this._loading.set(true);

        this._orderWizardApi
            .save(orderWizardDto)
            .pipe(finalize(() => this._loading.set(false)))
            .subscribe({
                next: (orderWizard) => {
                    orderWizard = this._corpTranslateService.translate(orderWizard);
                    //mise a jour du state
                    this._orderWizard.set(orderWizard);

                    //mise a jour session
                    this.updateSession({
                        sessionId: '',
                        requestType: 'Save',
                        currentUrl: window.location.href,
                        wizardData: this._orderWizard(),
                    }).subscribe();

                    //passage au paiement
                    window.location.href = this._stripeRedirectUrl()!;
                },
                error: (err) => {
                    this._router.navigate(['/order-page/payment-result'], {
                        queryParams: {
                            success: false,
                        },
                    });
                    // 'order-page/payment-result'
                    console.error('Erreur api', err);
                },
            });
    }

    addCurrencyPurchase() {
        this._loadingCurrencyCreation.set(true);

        const orderDto = this._orderWizard()!.step01;
        this._orderWizardApi
            .addCurrencyPurchase(orderDto!)
            .pipe(
                tap(() => this._loadingCurrencyCreation.set(true)),
                finalize(() => this._loadingCurrencyCreation.set(false)),
            )
            .subscribe({
                next: (orderDto) => {
                    orderDto = this._corpTranslateService.translate(orderDto);
                    this._orderWizard.update((current) => {
                        if (!current) return current;
                        return {
                            ...current,
                            step01: {
                                ...orderDto,
                            },
                        };
                    });
                },
                error: (err) => {
                    console.error('Erreur api', err);
                },
            });
    }

    changeLanguage(languageCode: string) {
        this._orderWizard.update((current) => {
            var current = nullifyIfEmpty(current);
            if (!current) return current;

            return {
                ...current,
                languageCode: languageCode,
            };
        });
    }

    changeItem(index: number, item: CurrencyPurchaseDto) {
        this._loadingCurrencyValue.set(true);
        // console.log('changeItem-wizard', this._orderWizard());

        this._orderWizardApi
            .changeItem(item)
            .pipe(finalize(() => this._loadingCurrencyValue.set(false)))
            .subscribe({
                next: (currencyPurchase) => {
                    this._orderWizard.update((current) => {
                        // console.log('wizard_1', this._orderWizard());
                        var current = nullifyIfEmpty(current);
                        if (!current) return current;

                        const list = [...current.step01!.currencyPurchaseList!];
                        list[index] = currencyPurchase;

                        return {
                            ...current,
                            step01: {
                                ...current.step01!,
                                currencyPurchaseList: list,
                            },
                        };
                    });
                    //call order total update
                    this.changePricingSummary();
                },
                error: (err) => {
                    console.error('Erreur api', err);
                },
            });
    }

    changePricingSummary() {
        this._loadingCurrencyValue.set(true);

        this._orderWizardApi
            .changePricingSummary(this._orderWizard()?.step01!)
            .pipe(
                finalize(() => this._loadingCurrencyValue.set(false)),
            )
            .subscribe({
                next: (pricingSummary) => {
                    this._orderWizard.update((current) => {
                        var current = nullifyIfEmpty(current);
                        if (!current) return current;

                        let _pricingSummaryNext = { ...current.step01!.pricingSummary! };
                        _pricingSummaryNext = pricingSummary.pricingSummary!;

                        return {
                            ...current,
                            step01: {
                                ...current.step01!,
                                pricingSummary: _pricingSummaryNext,
                            },
                        };
                    });
                },
                error: (err) => {
                    console.error('Erreur api', err);
                },
            });
    }

    emailValidationSend(customerDto: CustomerDto) {
        this._loadingEmailValidation.set(true);
        this._orderWizardApi.emailValidationSend(customerDto).subscribe({
            next: (value) => {
                // console.log('email send, check your mail box');
                //maj state wizard
                this._orderWizard.update((current) => {
                    var current = nullifyIfEmpty(current);
                    if (!current) return current;
                    return {
                        ...current,
                        step03: {
                            ...current.step03!,
                            customerDto: value,
                        },
                    };
                });
                //maj session
                this.updateSession({
                    sessionId: '',
                    requestType: 'Save',
                    currentUrl: window.location.href,
                    wizardData: this._orderWizard(),
                }).subscribe();
                this._emailValidationIsSent.set(true);
                //on commence le pooling pour verifier si lutilisateur a cliquer sur le bouton pour valider son mail (flag isMailValidate de customer passe a true)
                this.emailValidationStartPolling(value.id!);
            },
            error: (err) => {
                console.error('Erreur api', err);
            },
        });
    }

    emailValidationTimeout() {
        const customerDto = this._orderWizard()?.step03?.customerDto;

        this._orderWizardApi.emailValidationSetTimeout(customerDto!).subscribe({
            next: (value) => {
                this._emailValidationIsTimeOut.set(true);
            },
            error: (err) => {
                console.error('Erreur api', err);
            },
        });
    }

    emailValidationStartPolling(idCustomer: number) {
        const timeout = this.emailValidationPoolingCountdown() ?? 0;
        const timeout$ = timer(timeout); // ⏱️ 3 minutes max
        const delayTime = 5000; // toutes les 5 secondes
        let isValidated = false;

        interval(delayTime)
            .pipe(
                finalize(() => this._loadingEmailValidation.set(false)),
                switchMap(() => this._orderWizardApi.emailCheckIsValidated(idCustomer)),
                // continue tant que ce n'est PAS validé
                takeWhile((res) => !res.isMailConfirmed, true),
                // stop après timeout
                takeUntil(timeout$),
            )
            .subscribe({
                next: (res) => {
                    if (res.isMailConfirmed) {
                        isValidated = true;
                        res.isAskMailConfirmed = false;
                        //maj state wizard
                        this._orderWizard.update((current) => {
                            var current = nullifyIfEmpty(current);
                            if (!current) return current;
                            return {
                                ...current,
                                step03: {
                                    ...current.step03!,
                                    customerDto: res,
                                },
                            };
                        });

                        //maj session
                        this.updateSession({
                            sessionId: '',
                            requestType: 'Save',
                            currentUrl: window.location.href,
                            wizardData: this._orderWizard(),
                        }).subscribe();
                        // console.log('✅ Email validé !');

                        // this.isValidated = true;
                    }
                    this._emailValidationPoolingCountdown.set(
                        this._emailValidationPoolingCountdown() - delayTime,
                    );
                },
                complete: () => {
                    if (!isValidated) {
                        this.emailValidationTimeout();
                        // console.log('⏰ Timeout, aucune validation');
                    }
                },
            });
    }

    changePickupPoint(pickupPointDto: PickupPointDto | null) {
        this._orderWizard.update((current) => {
            if (!current) return current;
            return {
                ...current,
                step02: {
                    ...pickupPointDto!,
                },
            };
        });
    }

    updateAfterPayment(orderWizardDto: OrderWizardDto) {
        this._orderWizard.update((current) => {
            if (!current) return orderWizardDto;
            return { ...orderWizardDto };
        });
    }

    patch(changes: Partial<OrderWizardDto>) {
        this._orderWizard.update((current) => {
            if (!current) return current;

            const hasChanged = Object.keys(changes).some(
                (key) => !this.isEqual((current as any)[key], (changes as any)[key]),
            );

            return hasChanged ? { ...current, ...changes } : current;
        });
    }

    isEqual(a: any, b: any): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    updateSession(request: SessionUpdateRequest): Observable<SessionUpdateRequest> {
        if (request.wizardData) {
            request.wizardData.languageCode = this._translateService.getCurrentLang();
        }
        return this._orderWizardApi.updateSession(request);
    }

    prevStep() {
        this._orderWizard.update((current) => {
            if (!current) return current;
            return {
                ...current,
                activeStep: current.activeStep - 1,
            };
        });
        this.updateSession({
            sessionId: '',
            requestType: 'PrevStep',
            currentUrl: window.location.href,
            wizardData: this._orderWizard(),
        }).subscribe();
    }

    nextStep() {
        console.log('next-wizard', this._orderWizard());
        this._orderWizard.update((current) => {
            if (!current) return current;
            return {
                ...current,
                activeStep: current.activeStep + 1,
            };
        });
        this.updateSession({
            sessionId: '',
            requestType: 'NextStep',
            currentUrl: window.location.href,
            wizardData: this._orderWizard()
        }).subscribe();
    }

    patchCustomer(changes: CustomerDto) {
        this._orderWizard.update((current) => {
            if (!current) return current;
            return {
                ...current,
                step03: {
                    ...current.step03!,
                    customerDto: changes,
                },
            };
        });
        this.updateSession({
            sessionId: '',
            requestType: 'PatchStep3',
            currentUrl: window.location.href,
            wizardData: this._orderWizard(),
        }).subscribe();
    }
}
